import React, { useState, useEffect, useRef } from 'react';
import { getAllChats, listenToChat, sendEncryptedMessage } from '../../services/chatService';
import '../../components/AIAesthetics.css';

function AdminConsultations() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState(null);
  const messagesEndRef = useRef(null);
  const prevMessagesLength = useRef(0);

  useEffect(() => {
    fetchSessions();
    if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
      Notification.requestPermission();
    }
  }, []);

  const fetchSessions = async () => {
    setIsLoading(true);
    const chats = await getAllChats();
    setSessions(chats);
    setIsLoading(false);
  };

  useEffect(() => {
    if (selectedSession) {
      let initialLoad = true;
      const unsubscribe = listenToChat(selectedSession.uid, selectedSession.sessionId, (msgs) => {
        setMessages(msgs);
        
        if (!initialLoad && msgs.length > prevMessagesLength.current) {
          const lastMsg = msgs[msgs.length - 1];
          // If the message is from the user, notify the admin
          if (lastMsg && lastMsg.sender === 'user') {
            setToastMessage(`New message from ${selectedSession.uid.substring(0,8)}...`);
            setTimeout(() => setToastMessage(null), 4000);
            
            if ("Notification" in window && Notification.permission === "granted") {
              new Notification("DevTracker Admin Consultations", { body: "A user just replied to an active chat session." });
            }
          }
        }
        
        prevMessagesLength.current = msgs.length;
        initialLoad = false;
        
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      });
      return () => unsubscribe();
    }
  }, [selectedSession]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedSession) return;

    const msg = replyText.trim();
    setReplyText('');
    
    // Send admin reply encrypted with target user's uid
    await sendEncryptedMessage(selectedSession.uid, selectedSession.sessionId, msg, 'ai');
    
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePredefinedReply = async (text) => {
    if (!selectedSession) return;
    await sendEncryptedMessage(selectedSession.uid, selectedSession.sessionId, text, 'ai');
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const predefinedReplies = [
    "Hello! How can I help you today?",
    "I'm looking into this for you right now.",
    "Your DNA Score is actively tracking correctly.",
    "Please describe the issue in more detail.",
    "I've resolved the issue. Let me know if you need anything else."
  ];

  return (
    <>
    {toastMessage && (
      <div className="chat-toast-notification">
        <i className="fa-solid fa-bell"></i> {toastMessage}
      </div>
    )}
    <div className="sarvam-dashboard-container animate-up" style={{display: 'flex', flexDirection: 'row', height: 'calc(100vh - 120px)'}}>
      {/* Sidebar List */}
      <div className="sarvam-left-sidebar" style={{width: '320px', borderRight: '1px solid var(--sarvam-border)'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
          <h3 className="sarvam-sidebar-title" style={{margin: 0}}>Active Consultations</h3>
          <button onClick={fetchSessions} style={{background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--brand-accent)'}}>
            <i className="fa-solid fa-rotate-right"></i>
          </button>
        </div>
        
        {isLoading ? (
          <div style={{color: 'var(--sarvam-text-secondary)', textAlign: 'center'}}>Loading sessions...</div>
        ) : (
          <div className="sarvam-session-list">
            {sessions.map(s => (
              <div 
                key={`${s.uid}-${s.sessionId}`} 
                className={`sarvam-session-item ${selectedSession?.sessionId === s.sessionId ? 'active' : ''}`}
                onClick={() => setSelectedSession(s)}
                style={{flexDirection: 'column', alignItems: 'flex-start', gap: '4px'}}
              >
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                  <span style={{fontWeight: 600}}>Session {s.sessionId.split('-')[1]}</span>
                  <span style={{fontSize: '0.7rem', color: 'var(--sarvam-text-secondary)'}}>
                    {new Date(s.updatedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </span>
                </div>
                <div style={{fontSize: '0.8rem', color: 'var(--sarvam-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%'}}>
                  {s.previewText}
                </div>
                <div style={{fontSize: '0.65rem', fontFamily: 'monospace', opacity: 0.6}}>UID: {s.uid.substring(0,8)}...</div>
              </div>
            ))}
            {sessions.length === 0 && (
              <div style={{color: 'var(--sarvam-text-secondary)', fontSize: '0.85rem', padding: '10px 0'}}>No active chats found.</div>
            )}
          </div>
        )}
      </div>

      {/* Main Chat Panel */}
      <div style={{flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--sarvam-bg)'}}>
        {selectedSession ? (
          <>
            <div className="sarvam-header" style={{borderBottom: '1px solid var(--sarvam-border)', borderRadius: 0}}>
              <div className="sarvam-header-left">
                <h2>{selectedSession.sessionId}</h2>
                <span className="sarvam-subtitle">End-to-End Encrypted Communication with {selectedSession.uid}</span>
              </div>
              <div className="sarvam-header-right">
                <button className="sarvam-pill-btn secondary" onClick={() => setSelectedSession(null)}>
                  <i className="fa-solid fa-xmark"></i> Close Tab
                </button>
              </div>
            </div>

            <div className="sarvam-chat-wrapper" style={{borderRadius: 0}}>
              <div className="sarvam-messages-area">
                <div className="sarvam-chat-history">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`sarvam-message ${msg.sender === 'ai' ? 'user' : 'ai'}`}>
                      {msg.sender === 'ai' && <div className="sarvam-avatar user-avatar" style={{background: 'var(--brand-accent)', color: '#000'}}><i className="fa-solid fa-headset"></i></div>}
                      {msg.sender === 'user' && <div className="sarvam-avatar"><i className="fa-solid fa-user"></i></div>}
                      <div className="sarvam-bubble">{msg.text}</div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="sarvam-input-section" style={{paddingBottom: '20px'}}>
                <div style={{display: 'flex', gap: '8px', overflowX: 'auto', width: '100%', maxWidth: '750px', paddingBottom: '12px', scrollbarWidth: 'none', margin: '0 20px'}}>
                  {predefinedReplies.map((reply, idx) => (
                    <button 
                      key={idx} 
                      className="sarvam-chip" 
                      style={{whiteSpace: 'nowrap', fontSize: '0.75rem', padding: '6px 12px'}}
                      onClick={() => handlePredefinedReply(reply)}
                    >
                      {reply}
                    </button>
                  ))}
                </div>

                <form className="sarvam-input-box" onSubmit={handleReply}>
                  <input 
                    type="text" 
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type an admin reply..."
                    autoFocus
                  />
                  <button type="submit" disabled={!replyText.trim()} className="sarvam-submit-btn">
                    <i className="fa-solid fa-paper-plane"></i>
                  </button>
                </form>
              </div>
            </div>
          </>
        ) : (
          <div className="sarvam-welcome-state" style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'}}>
            <i className="fa-solid fa-shield-halved" style={{fontSize: '3rem', color: 'var(--sarvam-text-secondary)', opacity: 0.3, marginBottom: '20px'}}></i>
            <h1 className="sarvam-serif-title" style={{fontSize: '2rem'}}>Select a session to reply</h1>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default AdminConsultations;
